import {
	Anchor,
	DateString,
	Icon,
	Space,
	TransparentButtonList
} from "client/Components";
import { isNotNilOrEmpty, setTrackingId } from "client/Misc/utils";
import { MessageType } from "client/Api/Email/email";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./Meeting.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { useCalendarClient } from "client/Features/Calendar/useCalendarClient";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";

export type MeetingProps = {
	start: string | nil;
	end: string | nil;
	location: string | nil;
	messageType: MessageType;
	onAcceptMeetingInvite(): void;
	onDeclineMeetingInvite(): void;
	timezone: TimeZone;
};

export const Meeting = (props: MeetingProps): ReactElement => {
	const {
		start,
		end,
		location,
		messageType,
		onAcceptMeetingInvite,
		onDeclineMeetingInvite,
		timezone
	} = props;

	const [calendarClientElement, calendarClient] = useCalendarClient({});

	const startDate = useMemo(
		(): DateOnly | undefined => {
			if (!isNotNilOrEmpty(start)) {
				return undefined;
			}

			return convertToDateOnly(
				parseDtoDate(start),
				timezone
			);
		},
		[start, timezone]
	);

	const openCalendarClickHandler = useCallback(
		(): void => {
			if (!calendarClient.doesSupportOverview) {
				throw new Error("Client does not support overview.");
			}

			calendarClient.openOverview({
				isInNewWindow: true,
				date: startDate
			});
		},
		[calendarClient, startDate]
	);

	return (
		<>
			<div
				{...setTrackingId("meeting-information")}
				className={styles.information}
			>
				<div className={styles.icon}>
					<Icon
						size="extra-extra-large"
						value={{
							kind:"font",
							name:"calendar",
							color:"primary"
						}}
					/>
				</div>
				<div className={styles.time}>
					<span className={styles.label}>{localize("_When")}</span>
					{isNotNilOrEmpty(start) && isNotNilOrEmpty(end) && (
						<>
							<Space />
							<time
								{...setTrackingId("start-time")}
								dateTime={start}
							>
								<DateString
									value={start}
									format="DMYT"
									timezone={timezone}
								/>
							</time>
							<Space />
							-
							<Space />
							<time
								{...setTrackingId("end-time")}
								dateTime={end}
							>
								<DateString
									value={end}
									format="DMYT"
									timezone={timezone}
								/>
							</time>
						</>
					)}
				</div>
				<address className={styles.location}>
					<span className={styles.label}>{localize("_Where")}</span>
					<Space />
					<span
						{...setTrackingId("location")}
					>
						{location}
					</span>
				</address>
				{messageType === MessageType.MeetingRequest && (
					<div className={styles.buttonList}>
						<TransparentButtonList trackingId="meeting-request-actions">
							<TransparentButtonList.Item
								icon="check"
								text={localize("_Accept")}
								onClick={onAcceptMeetingInvite}
								trackingId="accept"
							/>
							<TransparentButtonList.Item
								icon="xmark"
								text={localize("_Decline")}
								onClick={onDeclineMeetingInvite}
								trackingId="decline"
							/>
						</TransparentButtonList>
					</div>
				)}
				{calendarClient.doesSupportOverview && (
					<div className={styles.openCalender}>
						<Anchor
							onClick={openCalendarClickHandler}
							trackingId="open-meeting-in-calendar"
						>
							{localize("_OpenCalendar")}
						</Anchor>
					</div>
				)}
			</div>
			{calendarClientElement}
		</>
	);
};
Meeting.displayName = "Meeting";